<template>
	<v-container class="main-container" fluid>
		<v-card class="login-card">
			<v-card-title class="modal-header login-title">{{ $t('login') }}</v-card-title>
			<v-divider></v-divider>
			<v-card-text class="login-body">
				<form class="modal-body login-form" @submit.prevent="login">
					<div class="field-block">
						<v-text-field
							ref="loginInputField"
							class="input user-input"
							:placeholder="`${$t('username')} ${$t('or')} ${$t('email')}`"
							v-model="loginInput"
							:error="showLoginInputError"
							:error-messages="loginInputError"
							outlined
							dense
							hide-details="auto"
							autocomplete="username"
							:disabled="isLoading || isSsoLoading"
							@input="onCredentialsInput"
						></v-text-field>
					</div>

					<div class="field-block">
						<v-text-field
							ref="passwordField"
							class="input password-input"
							:placeholder="$t('password')"
							v-model="password"
							:type="showPassword ? 'text' : 'password'"
							:error="showPasswordError"
							:error-messages="passwordError"
							:append-icon="showPassword ? eyeOffIcon : eyeIcon"
							outlined
							dense
							hide-details="auto"
							autocomplete="current-password"
							:disabled="isLoading || isSsoLoading"
							@click:append="showPassword = !showPassword"
							@input="onCredentialsInput"
						></v-text-field>
						<div class="forgot-password-row">
							<router-link class="forgot-password-link" to="/forgot-password">
								{{ $t('forgot_password') }}
							</router-link>
						</div>
					</div>

					<div class="terms-and-conditions" :class="{ 'terms-and-conditions--error': !!termsErrorText }">
						<v-checkbox
							ref="checkbox"
							class="custom-checkbox"
							v-model="isAgreedToTerms"
							hide-details
							:disabled="isLoading || isSsoLoading"
						/>
						<div class="terms-text">
							{{ $t("By logging in, you agree to Buzzztech's") }}
							<a
								class="link"
								href="https://buzzztech.com/en/terms-and-conditions/"
								target="_blank"
								rel="noopener noreferrer"
							>{{ $t('Terms and Conditions') }}</a>
							{{ $t('and') }}
							<a
								class="link"
								href="https://buzzztech.com/en/privacy-policy/"
								target="_blank"
								rel="noopener noreferrer"
							>{{ $t('Privacy Policy') }}</a>.
							{{ $t('All actions within this panel are recorded for quality control and improvement') }}.
						</div>
					</div>
					<p v-if="termsErrorText" class="field-error" role="alert">{{ termsErrorText }}</p>
					<p v-if="systemErrorText" class="system-error" role="alert">{{ systemErrorText }}</p>
					<p v-if="ssoErrorText" class="system-error" role="alert">{{ ssoErrorText }}</p>
				</form>
			</v-card-text>
			<v-card-actions class="buttons-container login-actions">
				<Button skin="primary" :disabled="isLoading || isSsoLoading" @click="login">
					{{ isLoading ? $t('loading') : $t('login') }}
				</Button>
				<template v-if="entraSsoEnabled">
					<div class="login-or-divider" role="separator">
						<span>{{ $t('login_or_divider') }}</span>
					</div>
					<button
						type="button"
						class="microsoft-sso-btn"
						:disabled="isLoading || isSsoLoading"
						@click="loginWithMicrosoft"
					>
						{{ isSsoLoading ? $t('loading') : $t('sign_in_with_microsoft') }}
					</button>
				</template>
			</v-card-actions>
		</v-card>
	</v-container>
</template>

<script>
import { mdiEye, mdiEyeOff } from '@mdi/js'
import Button from '@/modules/common/components/Button'
import swalService from '@/modules/common/services/swalService'
import authService from '@/modules/auth/services/authService'
import { isValidEmail, focusField } from '@/modules/auth/passwordRules'

const AUTH_ERROR_I18N = {
	INVALID_CREDENTIALS: 'login_invalid_credentials',
	CREDENTIALS_REQUIRED: 'login_credentials_required',
	LOGIN_NETWORK_ERROR: 'login_network_error',
	LOGIN_FAILED: 'login_failed',
	TOO_MANY_ATTEMPTS: 'login_too_many_attempts'
}

const SSO_ERROR_I18N = {
	SSO_USER_NOT_FOUND: 'sso_user_not_found',
	SSO_NOT_CONFIGURED: 'sso_not_configured',
	SSO_EMAIL_MISSING: 'sso_email_missing',
	SSO_STATE_INVALID: 'sso_state_invalid',
	SSO_PROVIDER_ERROR: 'sso_provider_error',
	SSO_EMAIL_REQUIRED: 'sso_email_missing',
	SSO_FAILED: 'sso_failed'
}

export default {
	components: { Button },
	data() {
		return {
			loginInput: '',
			password: '',
			isAgreedToTerms: false,
			hasSubmitted: false,
			hasAttemptedSso: false,
			isLoading: false,
			isSsoLoading: false,
			showPassword: false,
			localFieldError: '', // CREDENTIALS_REQUIRED from client validation
			ssoErrorCode: '',
			entraSsoEnabled: false,
			eyeIcon: mdiEye,
			eyeOffIcon: mdiEyeOff
		}
	},
	computed: {
		authErrorCode() {
			return this.localFieldError || this.$store.getters.authErrorMsg || ''
		},
		authErrorText() {
			const key = AUTH_ERROR_I18N[this.authErrorCode]
			return key ? this.$t(key) : ''
		},
		ssoErrorText() {
			if (!this.ssoErrorCode) return ''
			const key = SSO_ERROR_I18N[this.ssoErrorCode] || 'sso_failed'
			return this.$t(key)
		},
		isMissingLoginInput() {
			return this.hasSubmitted && !(this.loginInput || '').trim()
		},
		isMissingPassword() {
			return this.hasSubmitted && !this.password
		},
		showLoginInputError() {
			return this.isMissingLoginInput || this.authErrorCode === 'INVALID_CREDENTIALS'
		},
		showPasswordError() {
			return this.isMissingPassword
				|| this.authErrorCode === 'INVALID_CREDENTIALS'
				|| this.authErrorCode === 'CREDENTIALS_REQUIRED'
		},
		loginInputError() {
			if (this.isMissingLoginInput) return this.$t('login_credentials_required')
			return ''
		},
		passwordError() {
			// Wrong password / unknown user: message lives under the password field
			if (this.authErrorCode === 'INVALID_CREDENTIALS') return this.authErrorText
			if (this.isMissingPassword && !this.isMissingLoginInput) return this.$t('login_credentials_required')
			if (this.authErrorCode === 'CREDENTIALS_REQUIRED' && !this.isMissingLoginInput) {
				return this.authErrorText
			}
			return ''
		},
		systemErrorText() {
			if (
				this.authErrorCode === 'LOGIN_NETWORK_ERROR'
				|| this.authErrorCode === 'LOGIN_FAILED'
				|| this.authErrorCode === 'TOO_MANY_ATTEMPTS'
			) {
				return this.authErrorText
			}
			return ''
		},
		termsErrorText() {
			if ((this.hasSubmitted || this.hasAttemptedSso) && !this.isAgreedToTerms) {
				return this.$t('You must agree to the terms and conditions')
			}
			return ''
		}
	},
	async created() {
		this.entraSsoEnabled = await authService.isEntraSsoEnabled()
		await this.handleSsoReturn()
	},
	methods: {
		clearAuthError() {
			this.localFieldError = ''
			this.ssoErrorCode = ''
			if (this.$store.getters.authErrorMsg) {
				this.$store.commit({ type: 'setAuthError', error: '' })
			}
		},
		onCredentialsInput() {
			this.clearAuthError()
		},
		async handleSsoReturn() {
			const query = this.$route.query || {}
			if (query.ssoError) {
				this.ssoErrorCode = String(query.ssoError)
				this.$router.replace({ path: '/login', query: {} }).catch(() => {})
				return
			}
			if (query.sso !== 'success') return

			this.isSsoLoading = true
			try {
				const user = await this.$store.dispatch('getLoggedInUser')
				this.$router.replace({ path: '/login', query: {} }).catch(() => {})
				if (!user) {
					this.ssoErrorCode = 'SSO_FAILED'
					return
				}
				await this.finishLogin(user)
			} finally {
				this.isSsoLoading = false
			}
		},
		loginWithMicrosoft() {
			this.hasAttemptedSso = true
			this.clearAuthError()
			if (!this.isAgreedToTerms) {
				return
			}
			this.isSsoLoading = true
			authService.startEntraSso()
		},
		async login(ev) {
			if (ev && ev.preventDefault) ev.preventDefault()
			this.hasSubmitted = true
			this.clearAuthError()

			if (!this.isAgreedToTerms) {
				return
			}

			const trimmedInput = (this.loginInput || '').trim()
			if (!trimmedInput || !this.password) {
				this.localFieldError = 'CREDENTIALS_REQUIRED'
				this.$nextTick(() => {
					focusField(!trimmedInput ? this.$refs.loginInputField : this.$refs.passwordField)
				})
				return
			}

			this.isLoading = true
			try {
				const credentials = isValidEmail(trimmedInput)
					? { email: trimmedInput, password: this.password }
					: { username: trimmedInput, password: this.password }
				const user = await this.$store.dispatch({ type: 'login', credentials })
				if (!user) {
					this.$nextTick(() => focusField(this.$refs.passwordField))
					return
				}

				await this.finishLogin(user)
			} finally {
				this.isLoading = false
			}
		},
		async finishLogin(user) {
			await this.$store.dispatch({ type: 'loadFilterOptionsAndSelection' })
			const { siteIdsToShow, roles } = user
			if (user.level === 'installer') {
				swalService.errorMsg()
				authService.logout()
				return
			}
			if (siteIdsToShow.length > 0) {
				this.redirectBasedOnRole(roles)
			} else if (user.type === 'admin') {
				this.redirectAdminBasedOnRole(roles)
			}
		},
		redirectBasedOnRole(roles) {
			const roleRoutes = {
				dashboard: '/dashboard',
				'real-time-cleaning': '/real-time',
				'real-time-maintenance': '/real-time/maintenance',
				'cleaner-activity': '/cleaner-activity',
				'pad-presence-testing': '/pad-presence-testing',
				survey: '/survey',
				room: '/admin/room/list',
				task: '/task',
				users: '/users',
				shift: '/shift'
			}

			for (const role of roles) {
				if (roleRoutes[role]) {
					return this.$router.push(roleRoutes[role])
				}
			}
		},
		redirectAdminBasedOnRole(roles) {
			const adminRoutes = {
				company: '/admin/company',
				site: '/admin/site',
				user: '/admin/user'
			}

			for (const role of roles) {
				if (adminRoutes[role]) {
					return this.$router.push(adminRoutes[role])
				}
			}
		},
	}
}
</script>

<style lang="scss" scoped>
@use '../styles/authCard';

.main-container {
	.login-actions {
		flex-direction: column;
		align-items: stretch;
		gap: 0;
	}

	.login-or-divider {
		display: flex;
		align-items: center;
		gap: 10px;
		margin: 10px 0;
		width: 100%;
		font-size: 12px;
		font-weight: 600;
		letter-spacing: 0.04em;
		text-transform: uppercase;
		color: #8a94a6;

		&::before,
		&::after {
			content: '';
			flex: 1;
			height: 1px;
			background: #e2e8f0;
		}
	}

	.microsoft-sso-btn {
		width: 100%;
		min-height: 40px;
		border: 1px solid #c5cdd8;
		border-radius: 6px;
		background: #fff;
		color: #1f2937;
		font-size: 14px;
		font-weight: 600;
		cursor: pointer;

		&:hover:not(:disabled) {
			background: #f5f7fa;
			border-color: #9aa8bc;
		}

		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}
	}

	.terms-and-conditions {
		display: flex;
		align-items: flex-start;
		margin-top: 4px;
		padding: 8px 8px;
		border-radius: 6px;
		border: 1px solid transparent;

		&--error {
			border-color: #ef9a9a;
			background: #fff5f5;
		}

		.custom-checkbox {
			margin: 0 3px;
			flex: 0 0 auto;
		}

		.terms-text {
			font-size: 14px;
			line-height: 1.5;
			color: #5f6b7a;
			padding-top: 6px;
		}

		.link {
			color: #00afd4;
			font-weight: 600;
			text-decoration: none;
			padding: 0;
			height: auto;

			&:hover {
				text-decoration: underline;
			}
		}
	}
}
</style>
